/**
 * KOS Mobile Card Carousel
 * Handles horizontal scroll carousel with pagination dots
 */

(function () {
  'use strict';

  // Initialize all carousels on the page
  function initCarousels() {
    const carouselSections = document.querySelectorAll('.kos-mobile-card');

    carouselSections.forEach((section) => {
      const sectionId = section.getAttribute('data-section-id');
      if (!sectionId) {
        console.warn('KOS Mobile Card: Section missing data-section-id');
        return;
      }

      // Get the scrollable container (parent with overflow-x)
      const scrollableContainer = section.querySelector('.kos-mobile-card__cards-container');
      const cardsContainer = section.querySelector(`#kos-mobile-cards-${sectionId}`);
      const paginationContainer = section.querySelector(`#kos-mobile-pagination-${sectionId}`);

      if (!scrollableContainer) {
        console.warn('KOS Mobile Card: Scrollable container not found for section', sectionId);
        return;
      }

      if (!cardsContainer) {
        console.warn('KOS Mobile Card: Cards container not found for section', sectionId);
        return;
      }

      const cards = cardsContainer.querySelectorAll('.kos-mobile-card-item');
      const totalCards = cards.length;


      if (totalCards === 0) {
        console.warn('KOS Mobile Card: No cards found');
        return;
      }

      // Hide pagination if only one card
      if (totalCards <= 1) {
        if (paginationContainer) {
          paginationContainer.style.display = 'none';
        }
        return;
      }

      // Only proceed if pagination container exists
      if (!paginationContainer) {
        console.warn('KOS Mobile Card: Pagination container not found');
        return;
      }

      // Generate pagination dots
      generatePaginationDots(paginationContainer, totalCards, scrollableContainer, cards);

      // Set up scroll listener on the scrollable container
      setupScrollListener(scrollableContainer, paginationContainer, cards);

      // Initial update of indicators
      const initialIndex = getCurrentCardIndex(scrollableContainer, cards);
      updateActiveDot(paginationContainer, initialIndex);
    });
  }

  /**
   * Generate pagination dots
   */
  function generatePaginationDots(container, totalCards, scrollableContainer, cards) {
    container.innerHTML = '';

    for (let i = 0; i < totalCards; i++) {
      const dot = document.createElement('button');
      dot.className = 'kos-mobile-card__pagination-dot';
      dot.setAttribute('data-index', i);
      dot.setAttribute('aria-label', `Go to card ${i + 1} of ${totalCards}`);
      dot.setAttribute('role', 'tab');

      if (i === 0) {
        dot.classList.add('active');
        dot.setAttribute('aria-selected', 'true');
        dot.setAttribute('tabindex', '0');
      } else {
        dot.setAttribute('aria-selected', 'false');
        dot.setAttribute('tabindex', '-1');
      }

      // Click handler
      dot.addEventListener('click', function () {
        const targetIndex = parseInt(this.getAttribute('data-index'), 10);
        scrollToCard(scrollableContainer, cards, targetIndex);
        updateActiveDot(container, targetIndex);
      });

      // Keyboard navigation
      dot.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const targetIndex = parseInt(this.getAttribute('data-index'), 10);
          scrollToCard(scrollableContainer, cards, targetIndex);
          updateActiveDot(container, targetIndex);
        }
      });

      container.appendChild(dot);
    }
  }

  /**
   * Scroll to specific card
   */
  function scrollToCard(container, cards, index) {
    if (index < 0 || index >= cards.length) return;

    const targetCard = cards[index];
    if (!targetCard) return;

    // For scroll snap, we can use scrollIntoView or calculate scroll position
    // Using scrollIntoView with smooth behavior works well with scroll-snap
    targetCard.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'start'
    });
  }

  /**
   * Update active dot based on scroll position
   */
  function updateActiveDot(container, activeIndex) {
    if (!container) {
      console.warn('KOS Mobile Card: updateActiveDot called with no container');
      return;
    }

    const dots = container.querySelectorAll('.kos-mobile-card__pagination-dot');
    if (dots.length === 0) {
      console.warn('KOS Mobile Card: No pagination dots found');
      return;
    }

    dots.forEach((dot, index) => {
      const isActive = index === activeIndex;
      dot.classList.toggle('active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      dot.setAttribute('tabindex', isActive ? '0' : '-1');
      dot.setAttribute('aria-current', isActive ? 'page' : 'false');
    });
  }

  /**
   * Determine which card is currently in view
   */
  function getCurrentCardIndex(container, cards) {
    if (!container || cards.length === 0) return 0;

    const containerRect = container.getBoundingClientRect();
    const scrollLeft = container.scrollLeft;
    const containerWidth = containerRect.width;

    // Calculate which card is most visible based on scroll position
    // Each card width is approximately: container width - gap (16px on each side = 32px total)
    const cardWidth = containerWidth - 32; // Account for padding on sides
    const gap = 16;

    // Find the index of the card that's currently most visible
    let currentIndex = Math.round(scrollLeft / (cardWidth + gap));

    // Ensure index is within bounds
    currentIndex = Math.max(0, Math.min(currentIndex, cards.length - 1));

    // Alternative method: find card with most area visible
    let bestIndex = currentIndex;
    let maxVisibleArea = 0;

    cards.forEach((card, index) => {
      const cardRect = card.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();

      // Calculate intersection
      const left = Math.max(cardRect.left, containerRect.left);
      const right = Math.min(cardRect.right, containerRect.right);
      const visibleWidth = Math.max(0, right - left);

      if (visibleWidth > maxVisibleArea) {
        maxVisibleArea = visibleWidth;
        bestIndex = index;
      }
    });

    return bestIndex;
  }

  /**
   * Set up scroll listener with throttling
   */
  function setupScrollListener(container, paginationContainer, cards) {
    if (!container) {
      console.warn('KOS Mobile Card: setupScrollListener called with no container');
      return;
    }

    let scrollTicking = false;
    let lastKnownIndex = -1;

    const handleScroll = () => {
      if (!scrollTicking) {
        requestAnimationFrame(() => {
          const currentIndex = getCurrentCardIndex(container, cards);

          // Always update on first call or when index changed
          if (lastKnownIndex === -1 || currentIndex !== lastKnownIndex) {
            lastKnownIndex = currentIndex;
            updateActiveDot(paginationContainer, currentIndex);
          }

          scrollTicking = false;
        });
        scrollTicking = true;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });

    // Also listen for scroll end to ensure final state is correct
    let scrollTimeout;
    container.addEventListener('scroll', () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        const currentIndex = getCurrentCardIndex(container, cards);
        updateActiveDot(paginationContainer, currentIndex);
      }, 150);
    }, { passive: true });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCarousels);
  } else {
    initCarousels();
  }
})();
